import { useEffect, useRef, useState } from "react";
import { Html5Qrcode } from "html5-qrcode";
import { Award, Camera, CameraOff, CheckCircle2, Download, MessageCircle, ScanLine, XCircle } from "lucide-react";
import { API, api, formatDate } from "@/lib/api";
import { WhatsAppRemindersModal } from "./WhatsAppModal";

export const AdminCheckin = ({ initialEvent = "" }) => {
  const [stats, setStats] = useState(null);
  const [eventId, setEventId] = useState(initialEvent);
  const [wa, setWa] = useState(false);
  const [code, setCode] = useState("");
  const [result, setResult] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [camErr, setCamErr] = useState("");
  const [certMsg, setCertMsg] = useState("");
  const downloadCsv = async () => {
    const res = await fetch(`${API}/admin/events/${eventId}/attendance.csv`, { credentials: "include" });
    if (!res.ok) return setCertMsg("Could not download attendance.");
    const a = document.createElement("a"); a.href = URL.createObjectURL(await res.blob()); a.download = `attendance-${eventId}.csv`; a.click(); URL.revokeObjectURL(a.href);
  };
  const sendCerts = async () => {
    if (!window.confirm("Email participation certificates to every checked-in attendee of this event?")) return;
    setCertMsg("Sending…");
    try { setCertMsg((await api(`/admin/events/${eventId}/certificates`, { method: "POST" })).message); } catch (e) { setCertMsg(e.message); }
  };
  const scanner = useRef(null);
  const lastCode = useRef("");

  const load = () => api(`/admin/checkin/stats?event_id=${encodeURIComponent(eventId)}`).then(setStats).catch(() => {});
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [eventId]);

  const submit = async raw => {
    const value = (raw || "").trim();
    if (!value) return;
    try {
      const r = await api("/admin/checkin", { method: "POST", body: JSON.stringify({ code: value }) });
      setResult({ ok: r.status === "ok", warn: r.status === "already", message: r.message, reg: r.registration });
    } catch (e) { setResult({ ok: false, message: e.message }); }
    setCode(""); load();
  };

  const startCamera = async () => {
    setCamErr("");
    try {
      const h = new Html5Qrcode("qr-reader");
      scanner.current = h;
      await h.start({ facingMode: "environment" }, { fps: 8, qrbox: { width: 240, height: 240 } }, text => {
        if (text === lastCode.current) return;
        lastCode.current = text;
        submit(text);
        setTimeout(() => { lastCode.current = ""; }, 4000);
      }, () => {});
      setScanning(true);
    } catch (e) { setCamErr("Camera unavailable — allow camera access or type the code below."); }
  };
  const stopCamera = async () => { try { await scanner.current?.stop(); scanner.current?.clear(); } catch {} setScanning(false); };
  useEffect(() => () => { scanner.current?.stop?.().catch(() => {}); }, []);

  return (
    <div className="checkin" data-testid="admin-checkin">
      <div className="metric-grid">
        <div className="metric"><ScanLine size={20} /><strong data-testid="checkin-attended">{stats?.attended ?? "–"}</strong><span>Checked in</span></div>
        <div className="metric"><CheckCircle2 size={20} /><strong data-testid="checkin-total">{stats?.total ?? "–"}</strong><span>Registered</span></div>
        <div className="metric gold"><strong>{stats?.total ? Math.round((stats.attended / stats.total) * 100) : 0}%</strong><span>Attendance</span></div>
        <label className="metric" style={{ cursor: "default" }}>
          <span>Filter by event</span>
          <select value={eventId} onChange={e => setEventId(e.target.value)} data-testid="checkin-event-filter">
            <option value="">All events</option>
            {stats?.events?.map(e => <option key={e.id} value={e.id}>{e.title}</option>)}
          </select>
        </label>
      </div>
      {eventId && (
        <div className="hint" style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }} data-testid="event-report-bar">
          <Award size={16} /> Event report:
          <button type="button" className="button button-ghost dark" onClick={downloadCsv} data-testid="attendance-csv-button"><Download size={14} /> Attendance CSV</button>
          <button type="button" className="button button-gold" onClick={sendCerts} data-testid="send-certificates-button"><Award size={14} /> Email certificates</button>
          <button type="button" className="button button-ghost dark" onClick={() => setWa(true)} data-testid="whatsapp-reminders-button"><MessageCircle size={14} /> WhatsApp reminders</button>
          {certMsg && <span data-testid="certificates-status">{certMsg}</span>}
        </div>
      )}
      {wa && eventId && <WhatsAppRemindersModal eventId={eventId} onClose={() => setWa(false)} />}
      <div className="checkin-grid">
        <section className="account-card">
          <h3><Camera size={18} /> Scan QR ticket</h3>
          <div id="qr-reader" className="qr-reader" data-testid="qr-reader" />
          {camErr && <div className="hint" data-testid="camera-error">{camErr}</div>}
          {scanning ? (
            <button type="button" className="button button-ghost dark" onClick={stopCamera} data-testid="camera-stop"><CameraOff size={14} /> Stop camera</button>
          ) : (
            <button type="button" className="button button-gold" onClick={startCamera} data-testid="camera-start"><Camera size={14} /> Start camera</button>
          )}
          <form onSubmit={e => { e.preventDefault(); submit(code); }} className="checkin-manual" data-testid="checkin-manual-form">
            <input value={code} onChange={e => setCode(e.target.value)} placeholder="…or type / paste the ticket code" data-testid="checkin-code-input" />
            <button className="button button-ghost dark" data-testid="checkin-submit">Check in</button>
          </form>
          {result && (
            <div className={result.ok ? "success-message" : result.warn ? "hint" : "error-message"} data-testid="checkin-result">
              {result.ok ? <CheckCircle2 size={18} /> : <XCircle size={18} />} <b>{result.message}</b>
              {result.reg && <small style={{ display: "block", marginTop: 6 }}>{result.reg.event_title} · Ref {result.reg.id}</small>}
            </div>
          )}
        </section>
        <section className="account-card" data-testid="checkin-recent">
          <h3><CheckCircle2 size={18} /> Recent check-ins</h3>
          {stats?.recent?.length ? stats.recent.map(r => (
            <div className="list-row" key={r.id}><div><b>{r.name}</b><small>{r.event_title}</small></div><span className="pill paid">{formatDate(r.checked_in_at)}</span></div>
          )) : <div className="hint">No one has checked in yet.</div>}
        </section>
      </div>
    </div>
  );
};
